<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="items.items"
      item-key="id"
      class="elevation-0 background-transparent"
      :loading="loading"
      :options.sync="options"
      :server-items-length="items.meta.totalItems"
      :footer-props="{
        'items-per-page-options': rowsPerPageItemsGlobal
      }"
      data-cy="entities-datatable"
    >
      <template v-slot:top>
        <div class="d-inline-flex justify-space-between align-center pr-2 mb-5" style="width: 100%">

          <!-- Left-aligned elements -->
          <div class="d-inline-flex align-center">
            <v-switch
              v-model="activeOnly"
              data-cy="switch-active-only"
              :label="$lang.labels.activeOnly"
              class="pl-2 pr-3"
              inset
              dense
            ></v-switch>
            <v-text-field
              v-model="search"
              class="custom-search-perm"
              outlined
              clearable
              dense
              hide-details
              :label="$lang.labels.searchByName"
              data-cy="search-by-name"
            ></v-text-field>
            <v-select
              v-model="permissionType"
              class="ml-2 custom-search-perm"
              hide-details
              :items="typesFormatted"
              :label="$lang.labels.permissionType"
              outlined
              dense
              clearable
              data-cy="permission-type"
            ></v-select>
          </div>

          <!-- Right-aligned buttons -->
          <div class="d-inline-flex align-center ml-auto">
            <v-btn
              text
              light
              class="ml-2"
              color="primary"
              data-cy="refresh"
              @click="$emit('fetchEntities', { options: options, search: search && search.length > 1 ? search : '', permissionType: permissionType, resetPage: true })"
            >
              <v-icon dense small>mdi-refresh</v-icon>
            </v-btn>
            <v-menu
              offset-y
              nudge-left="64"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="roles.includes('ENTITY_CREATOR')"
                  outlined
                  color="accent"
                  class="color-accent-text mr-1"
                  data-cy="create-entity"
                  v-on="on"
                >
                  <v-icon
                    left
                    dark
                  >
                    mdi-file-plus
                  </v-icon>
                  {{ $lang.actions.create }}
                </v-btn>
              </template>

              <v-card class="pr-2 pl-1" style="border: #CBD5E1 1px solid;">
                <v-list dense>
                  <v-list-item
                    link
                    class="text--secondary font-weight-light"
                    data-cy="create-custom-entity-button"
                    :to="{ name: 'entityCreate', params: { lang: $lang.current_lang } }"
                  >
                    <v-list-item-title>{{ $lang.labels.customEntity }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    class="text--secondary font-weight-light"
                    data-cy="generate-entity-button"
                    :to="{ name: 'entityGenerate', params: { lang: $lang.current_lang } }"
                  >
                    <v-list-item-title>{{ $lang.labels.generateEntity }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>

        </div>
      </template>

      <template v-slot:item.id="{ item }">
        <div class="font-weight-bold"># {{ item.id }}</div>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="d-flex justify-space-between align-center">
          <!-- Name on the left with a fixed or max width -->
          <div class="text-start name-container clickable-simple"> <!-- Adjust the margin-right here -->
            <router-link v-if="item.name.length < 86" class="text-decoration-none" :to="{ name: 'entityEdit', params: { id: item.id } }">
              <span class="font-weight-regular color-primary">{{ item.name }}</span>
            </router-link>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <router-link
                    class="text-decoration-none"
                    :to="{ name: 'entityEdit', params: { id: item.id } }"
                  >
                    <span class="font-weight-regular color-primary">{{ item.name.substring(0, 62) }}...</span>
                  </router-link>
                </div>
              </template>
              <span>{{ item.name }}</span>
            </v-tooltip>
          </div>
          <!-- 3 dots menu on the right -->
          <div>
            <v-menu class="custom-edit-menu" offset-y nudge-left="75" transition="slide-y-transition"> <!-- Adjust nudge-left if necessary -->
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  variant="text"
                  icon
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <!-- Menu with actions -->
              <v-card class="pr-2 pl-1" style="border: #CBD5E1 1px solid;">
                <v-list dense>
                  <!-- Deactivate/Activate button -->
                  <v-list-item
                    :disabled="item.isSystem"
                    link
                    class="text--secondary font-weight-light"
                    @click="$emit('changeStatus', {id: item.id, status: item.status})"
                  >
                    {{ item.status === 'ACTIVE' ? $lang.actions.deactivate : $lang.actions.activate }}
                  </v-list-item>

                  <!-- Copy Create button -->
                  <v-list-item
                    v-if="roles.includes('ENTITY_CREATOR')"
                    link
                    class="text--secondary font-weight-light"
                    @click="copyCreateResource(item, 'entityCreate', $router, '', $options.filters.formatDateTime(new Date()), false, true)"
                  >
                    {{ $lang.actions.copyCreate }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </div>
      </template>

      <template v-slot:item.createdOn="{ item }">
        <div>{{ item.createdOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.modifiedOn="{ item }">
        <div>{{ item.modifiedOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.status="{ item }">
        <div class="custom-active-deactive">
          <p :class="{'custom-active': item.status === 'ACTIVE', 'custom-deactivated': item.status !== 'ACTIVE', 'm-0 p-0 mb-0': true }">
            {{ $lang.status[item.status] }}
          </p>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { bus } from '@/main'
import { copyCreateResource } from '@/utils/helpers'
import { debounce } from 'lodash'

export default {
  props: {
    items: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      lock: true,
      showTypeSelector: false,
      activeOnly: true,
      search: '',
      permissionType: '',
      options: {},
      totalItems: 0,
      headers: [
        {
          text: this.$lang.labels.name,
          align: 'start',
          sortable: true,
          value: 'name'
        },
        { text: this.$lang.labels.status, value: 'status', sortable: true }
      ],
      roles: [],
      permissionTypes: ['EDIT', 'USE', 'VIEW']
    }
  },
  computed: {
    ...mapState('app', ['rowsPerPageItemsGlobal', 'userRoles', 'userSettings']),
    typesFormatted () {
      return this.permissionTypes.map((item) => {
        return { text: this.$lang.status[item], value: item }
      })
    }
  },
  watch: {
    options: {
      handler () {
        if (!this.lock) this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType })
        this.savePreFill()
      },
      deep: true
    },
    search: {
      handler: debounce( function (val) {
        if (!this.lock && val && val.length > 1) this.$emit('fetchEntities', { options: this.options, search: val, permissionType: this.permissionType, resetPage: true })
        if (!this.lock && val === null || val === '') this.$emit('fetchEntities', { options: this.options, search: '', permissionType: this.permissionType, resetPage: true })
        this.savePreFill()
        this.options.page = 1
      }, 500)
    },
    activeOnly: {
      handler (val) {
        if (!this.lock) this.$emit('activeOnly', { activeOnly: val, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true } })
        this.savePreFill()
        this.options.page = 1
      }
    },
    permissionType: {
      handler () {
        if (!this.lock) this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
        this.savePreFill()
        this.options.page = 1
      }
    }
  },
  mounted() {
    bus.$on('refreshData', (name) => {
      if (!this.lock && name === this.$route.name) this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
      this.options.page = 1
    })
  },
  created() {
    this.roles = this.userRoles.map((x) => x.name) || []

    if (this.userSettings.display.showModifiedOn) {
      this.headers.splice(1, 0, { text: this.$lang.labels.modifiedOn, value: 'modifiedOn' })
    }

    if (this.userSettings.display.showCreatedOn) {
      this.headers.splice(1, 0, { text: this.$lang.labels.createdOn, value: 'createdOn' })
    }

    if (this.userSettings.display.showId) {
      this.headers.splice(0, 0, { text: this.$lang.header.id, value: 'id' })
    }

    if (this.$route.query && Object.keys(this.$route.query).length > 0) {
      if (this.$route.query && this.$route.query.search) {
        this.search = this.$route.query.search
      }
      if (this.$route.query.activeOnly) {
        this.activeOnly = this.$route.query.activeOnly === 'true'
        this.$emit('activeOnly', { activeOnly: this.activeOnly, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType } })
      } else {
        this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
      }
    } else if (localStorage.preFillData) {
      const preFill = JSON.parse(localStorage.preFillData)

      if (preFill && preFill.entityList) {
        this.search = preFill.entityList.search
        this.activeOnly = preFill.entityList.activeOnly
        this.permissionType = preFill.entityList.permissionType
        this.options = preFill.entityList.options

        this.$emit('activeOnly', { activeOnly: this.activeOnly, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType } })
      } else {
        this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
      }
    } else {
      this.$emit('fetchEntities', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
    }
    this.options.page = 1
    setTimeout(() => {
      this.lock = false
    }, 100)
  },
  methods: {
    copyCreateResource,
    savePreFill() {
      if (!localStorage.preFillData) localStorage.preFillData = '{}'
      if (localStorage.preFillData) {
        const preFill = JSON.parse(localStorage.preFillData)

        if (!preFill.entityList) preFill.entityList = {
          search: '',
          activeOnly: true,
          permissionType: '',
          options: {
            page: 1,
            itemsPerPage: 25,
            sortBy: [],
            sortDesc: []
          }
        }

        preFill.entityList.search = this.search
        preFill.entityList.activeOnly = this.activeOnly
        preFill.entityList.permissionType = this.permissionType
        preFill.entityList.options = { ...this.options, page: 1 }

        localStorage.preFillData = JSON.stringify(preFill)
      }
    }
  }
}
</script>

<style>
.v-btn:before {
  background-color: transparent;
}
</style>
